import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { AxiosResponse } from "axios";

import { MenuItem } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import PendingInsuranceProviderRow from "./PendingInsuranceProviderRow";
import InsuranceService from "Services/InsuranceService/InsuranceService";
import { Loader } from "Shared/Common/Loader";
import NoRecord from "Shared/Common/NoRecord";
import Splash from "Shared/Common/Pages/Splash";
import PermissionComponent from "Shared/Common/Permissions/PermissionComponent";
import useIsMobile from "Shared/hooks/useIsMobile";
import useLang from "Shared/hooks/useLanguage";
import { ArrowDown, ArrowUp } from "Shared/Icons";
import CustomPagination from "Shared/JsxPagination";
import ArrowBottomIcon from "Shared/SVG/ArrowBottomIcon";
import { StringRecord } from "Shared/Type";
import { SortingTypeI } from "Utils/consts";
import { StyledDropButton, StyledDropMenu } from "Utils/Style/Dropdownstyle";

import useIsIphone from "Shared/hooks/useIsIphone";

export interface IRequisitionOrder {
  requisitonInsurancePatientId: number;
  requisitionOrderId: number;
  requisitionId: number;
  requisitionTypeId?: number;
  requisitionType: string;
  recordId: string;
  accessionNo: string | null;
  labName: string;
  facilityName: string;
  facilityId?: number;
}

export interface IInsuranceProviderRows {
  id: string;
  recordId: string;
  insuranceAssignmentId?: number;
  providerName: string;
  providerCode?: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  phoneNumber: string;
  file?: string;
  fileName?: string;
  requisitionOrders?: IRequisitionOrder[];
}

export default function PendingInsuranceProviderTab() {
  const [sort, setSorting] = useState<SortingTypeI>({
    clickedIconData: "",
    sortingOrder: "",
  });
  const searchRef = useRef<any>(null);
  const { t } = useLang();
  const isMobile = useIsMobile();
  const isIphone = useIsIphone();

  const [triggerSearchData, setTriggerSearchData] = useState(false);
  //============================================================================================
  //====================================  PAGINATION START =====================================
  //============================================================================================

  const [curPage, setCurPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState<number>(0);
  const [totalPages, setTotalPages] = useState(0);
  const [pageNumbers, setPageNumbers] = useState<number[]>([]);
  const nextPage = () => {
    if (curPage < Math.ceil(total / pageSize)) {
      setCurPage(curPage + 1);
    }
  };

  const showPage = (i: number) => {
    setCurPage(i);
  };

  const prevPage = () => {
    if (curPage > 1) {
      setCurPage(curPage - 1);
    }
  };

  useEffect(() => {
    setTotalPages(Math.ceil(total / pageSize));
    const pgNumbers = [];
    for (let i = curPage - 2; i <= curPage + 2; i++) {
      if (i > 0 && i <= totalPages) {
        pgNumbers.push(i);
      }
    }
    setPageNumbers(pgNumbers);
  }, [total, curPage, pageSize, totalPages]);

  useEffect(() => {
    loadGridData(true, false);
  }, [curPage, pageSize, triggerSearchData]);
  //============================================================================================
  //====================================  PAGINATION END =======================================
  //============================================================================================
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<IInsuranceProviderRows[]>(() => []);
  const [selectedProviderIds, setSelectedProviderIds] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  const initialSearchQuery = {
    providerName: "",
    providerCode: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    phoneNumber: "",
    recordId: "",
  };
  const queryDisplayTagNames: StringRecord = {
    providerName: "Provider Name",
    providerCode: "Provider Code",
    address: "Address",
    city: "City",
    state: "State",
    zipCode: "Zip Code",
    phoneNumber: "Phone Number",
    recordId: "Record ID",
  };
  const [searchRequest, setSearchRequest] = useState(initialSearchQuery);
  type InputChangeEvent = HTMLInputElement | HTMLSelectElement;
  const onInputChangeSearch = (e: React.ChangeEvent<InputChangeEvent>) => {
    setSearchRequest({ ...searchRequest, [e.target.name]: e.target.value });
  };
  useEffect(() => {
    loadGridData(true, true);
  }, []);
  const loadGridData = (
    loader: boolean,
    reset: boolean,
    _sortingState?: any
  ) => {
    if (loader) {
      setLoading(true);
    }
    const trimmedQuery = Object.fromEntries(
      Object.entries(searchRequest).map(([key, value]) => [
        key,
        typeof value === "string" ? value.trim() : value,
      ])
    );
    const nullObj = {
      providerName: null,
      providerCode: null,
      address: null,
      city: null,
      state: null,
      zipCode: null,
      phoneNumber: null,
      recordId: null,
    };

    const queryModel = reset ? nullObj : trimmedQuery;
    const normalizeOptionalField = (value?: string | null) => {
      const trimmedValue = typeof value === "string" ? value.trim() : value;
      return trimmedValue ? trimmedValue : null;
    };
    const queryModelWithStatus = {
      providerId: 0,
      providerName: normalizeOptionalField(queryModel.providerName),
      providerCode: normalizeOptionalField(queryModel.providerCode),
      address: normalizeOptionalField(queryModel.address),
      city: normalizeOptionalField(queryModel.city),
      state: normalizeOptionalField(queryModel.state),
      zipCode: normalizeOptionalField(queryModel.zipCode),
      phoneNumber: normalizeOptionalField(queryModel.phoneNumber),
      recordID: normalizeOptionalField(queryModel.recordId),
      image: null,
      insuranceStatus: "Pending",
    };

    // Get sort column and direction from sort state
    const sortColumn = sort?.clickedIconData || null;
    const sortDirection = sort?.sortingOrder || null;

    InsuranceService.GetInsuranceApprovalDetail({
      pageNumber: curPage,
      pageSize: pageSize,
      queryModel: queryModelWithStatus,
      sortColumn: sortColumn,
      sortDirection: sortDirection,
    })
      .then((res: AxiosResponse) => {
        setTotal(res?.data?.total);
        // Map API response to component expected format
        const mappedRows = (res?.data?.data || []).map((item: any) => {
          const providerId = item.providerId ?? item.ProviderId;
          const recordId = item.recordID ?? item.RecordID;
          const providerName = item.providerName ?? item.ProviderName;
          const providerCode = item.providerCode ?? item.ProviderCode;
          const address = item.address ?? item.Address;
          const city = item.city ?? item.City;
          const state = item.state ?? item.State;
          const zipCode = item.zipCode ?? item.ZipCode;
          const phoneNumber =
            item.phoneNumber ?? item.PhoneNumber ?? item.PhoneNo;
          const image = item.image ?? item.Image;
          const orders = (item.requisitionOrders ?? item.Orders ?? []).map(
            (order: any) => ({
              requisitonInsurancePatientId:
                order.requisitonInsurancePatientId ?? order.PatientInsuranceId,
              requisitionOrderId:
                order.requisitionOrderId ?? order.RequisitionOrderID,
              requisitionId: order.requisitionId ?? order.RequisitionID,
              requisitionTypeId:
                order.requisitionTypeId ?? order.RequisitionTypeId,
              requisitionType: order.requisitionType ?? order.RequisitionType,
              recordId: order.recordId ?? order.RecordID,
              accessionNo: order.accessionNo ?? order.AccessionNo,
              labName: order.labName ?? order.LabName,
              facilityName: order.facilityName ?? order.FacilityName,
              facilityId: order.facilityId ?? order.FacilityId,
            })
          );

          const insuranceAssignmentId = item.InsuranceAssignmentID;

          return {
            id: providerId?.toString() || "",
            recordId: recordId || "",
            insuranceAssignmentId: insuranceAssignmentId ?? undefined,
            providerName: providerName || "",
            providerCode: providerCode || "",
            address: address || "",
            city: city || "",
            state: state || "",
            zipCode: zipCode || "",
            phoneNumber: phoneNumber || "",
            file: image || "",
            fileName: image ? "File" : "",
            requisitionOrders: orders,
          };
        });
        setRows(mappedRows);
        setLoading(false);
      })
      .catch((err: any) => {
        console.trace(err, "err");
        setLoading(false);
      });
  };

  function resetSearch() {
    setSearchRequest({
      providerName: "",
      providerCode: "",
      address: "",
      city: "",
      state: "",
      zipCode: "",
      phoneNumber: "",
      recordId: "",
    });
    setSorting({
      clickedIconData: "",
      sortingOrder: "",
    });
    loadGridData(true, true);
  }
  const handleSelectProvider = (providerId: number, checked: boolean) => {
    if (checked) {
      setSelectedProviderIds((prev) => [...prev, providerId]);
    } else {
      setSelectedProviderIds((prev) => prev.filter((id) => id !== providerId));
      setSelectAll(false);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    if (checked) {
      const allIds = rows.map((row) => parseInt(row.id));
      setSelectedProviderIds(allIds);
    } else {
      setSelectedProviderIds([]);
    }
  };

  const handleApprove = async (id?: string) => {
    const providerIds = id ? [parseInt(id)] : selectedProviderIds;

    if (providerIds.length === 0) {
      toast.error(t("Please select at least one insurance provider"));
      return;
    }

    const providersToApprove = id
      ? rows.filter((row) => row.id === id)
      : rows.filter((row) => providerIds.includes(parseInt(row.id)));

    const insuranceAssignmentIds = providersToApprove
      .map((provider) => provider.insuranceAssignmentId)
      .filter(
        (assignmentId): assignmentId is number => assignmentId !== undefined
      );

    try {
      setLoading(true);
      await InsuranceService.InsuranceApprovalAction({
        action: "Approved",
        providerIds: providerIds,
        insuranceAssignmentIds: insuranceAssignmentIds,
      });
      toast.success(t("Insurance Provider approved successfully"));
      setSelectedProviderIds([]);
      setSelectAll(false);
      loadGridData(true, false);
    } catch (error: any) {
      console.error("Error approving insurance provider:", error);
      toast.error(
        error?.response?.data?.message ||
          t("Failed to approve insurance provider")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (id?: string) => {
    const providerIds = id ? [parseInt(id)] : selectedProviderIds;

    if (providerIds.length === 0) {
      toast.error(t("Please select at least one insurance provider"));
      return;
    }

    // Check if any selected provider has any requisitionOrders
    const providersToCheck = id
      ? rows.filter((row) => row.id === id)
      : rows.filter((row) => providerIds.includes(parseInt(row.id)));

    const hasRequisitionOrders = providersToCheck.some(
      (provider) =>
        provider.requisitionOrders && provider.requisitionOrders.length > 0
    );

    if (hasRequisitionOrders) {
      toast.error(t("First you need to change or remove the insurances"));
      return;
    }

    const insuranceAssignmentIds = providersToCheck
      .map((provider) => provider.insuranceAssignmentId)
      .filter(
        (assignmentId): assignmentId is number => assignmentId !== undefined
      );

    try {
      setLoading(true);
      await InsuranceService.InsuranceApprovalAction({
        action: "Rejected",
        providerIds: providerIds,
        insuranceAssignmentIds: insuranceAssignmentIds,
      });
      toast.success(t("Insurance Provider rejected successfully"));
      setSelectedProviderIds([]);
      setSelectAll(false);
      loadGridData(true, false);
    } catch (error: any) {
      console.error("Error rejecting insurance provider:", error);
      toast.error(
        error?.response?.data?.message ||
          t("Failed to reject insurance provider")
      );
    } finally {
      setLoading(false);
    }
  };

  const [anchorEl, setAnchorEl] = React.useState({
    dropdown1: null,
    dropdown2: null,
  });

  const handleClick = (event: any, dropdownName: any) => {
    setAnchorEl({ ...anchorEl, [dropdownName]: event.currentTarget });
  };

  const handleClose = (dropdownName: string) => {
    setAnchorEl({ ...anchorEl, [dropdownName]: null });
  };

  const handleSort = (columnName: any) => {
    searchRef.current.id = searchRef.current.id
      ? searchRef.current.id === "asc"
        ? (searchRef.current.id = "desc")
        : (searchRef.current.id = "asc")
      : (searchRef.current.id = "asc");

    setSorting({
      sortingOrder: searchRef?.current?.id,
      clickedIconData: columnName,
    });
  };

  const handleKeyPress = (e: any) => {
    if (e.key === "Enter") {
      setCurPage(1);
      setTriggerSearchData((prev) => !prev);
    }
  };

  useEffect(() => {
    loadGridData(true, false);
  }, [sort]);

  // Handling searchedTags
  const [searchedTags, setSearchedTags] = useState<string[]>([]);

  const handleTagRemoval = (clickedTag: string) => {
    setSearchRequest((prevSearchRequest: any) => {
      return {
        ...prevSearchRequest,
        [clickedTag]: (initialSearchQuery as any)[clickedTag],
      };
    });
  };

  useEffect(() => {
    const uniqueKeys = new Set<string>();
    for (const [key, value] of Object.entries(searchRequest)) {
      if (value) {
        uniqueKeys.add(key);
      }
    }
    setSearchedTags(Array.from(uniqueKeys));
  }, [searchRequest]);

  useEffect(() => {
    if (searchedTags.length === 0) resetSearch();
  }, [searchedTags.length]);

  useEffect(() => {
    // Sync selectAll state when rows change
    if (rows.length > 0 && selectedProviderIds.length === rows.length) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  }, [rows.length, selectedProviderIds.length]);

  return (
    <>
      <div className="col-xl-12 col-lg-12 col-md-12 col-sm-12">
        <div className="card mb-3 rounded">
          <div>
            <div className="d-flex gap-4 flex-wrap mb-2">
              {searchedTags.map((tag) =>
                tag === "isArchived" ? null : (
                  <div
                    key={tag}
                    className="d-flex align-items-center cursor-pointer gap-1 p-2 rounded bg-light"
                    onClick={() => handleTagRemoval(tag)}
                  >
                    <span className="fw-bold">
                      {t(queryDisplayTagNames[tag])}
                    </span>
                    <i className="bi bi-x"></i>
                  </div>
                )
              )}
            </div>
            <div className="align-items-center d-flex flex-column flex-md-row flex-wrap gap-0 gap-lg-0 justify-content-center justify-content-sm-between responsive-flexed-actions">
              <div className="align-items-center d-flex flex-column flex-sm-row gap-2 justify-content-center mb-2">
                <div className="d-flex align-items-center">
                  <span className="fw-400 mr-3">{t("Records")}</span>
                  <select
                    id="PendingInsuranceProviderRecords"
                    className="form-select w-125px h-33px rounded py-2"
                    data-kt-select2="true"
                    data-placeholder="Select option"
                    data-dropdown-parent="#kt_menu_63b2e70320b73"
                    data-allow-clear="true"
                    onChange={(e) => {
                      setPageSize(parseInt(e.target.value));
                    }}
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="50" selected>
                      50
                    </option>
                    <option value="100">100</option>
                  </select>
                  <div className="ps-2">
                    <StyledDropButton
                      id="PendingInsuranceProviderBulkAction"
                      aria-haspopup="true"
                      onClick={(event) => handleClick(event, "dropdown1")}
                      className="btn btn-info btn-sm"
                    >
                      {t("Bulk Action")}
                      <span className="svg-icon svg-icon-5 m-0">
                        <ArrowBottomIcon />
                      </span>
                    </StyledDropButton>
                    <StyledDropMenu
                      id="PendingInsuranceProviderBulkActionMenu"
                      aria-labelledby="demo-positioned-button1"
                      anchorEl={anchorEl.dropdown1}
                      open={Boolean(anchorEl.dropdown1)}
                      onClose={() => handleClose("dropdown1")}
                      anchorOrigin={{
                        vertical: "top",
                        horizontal: "left",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "left",
                      }}
                    >
                      <PermissionComponent
                        moduleName="Setup"
                        pageName="Insurance Provider Request"
                        permissionIdentifier="BulkApprove"
                      >
                        <MenuItem
                          id="PendingInsuranceProviderBulkApprove"
                          onClick={() => {
                            handleClose("dropdown1");
                            handleApprove();
                          }}
                          className="w-125px"
                        >
                          <i className="fa fa-check text-success mr-2 w-20px"></i>
                          {t("Approve")}
                        </MenuItem>
                      </PermissionComponent>
                      <PermissionComponent
                        moduleName="Setup"
                        pageName="Insurance Provider Request"
                        permissionIdentifier="BulkReject"
                      >
                        <MenuItem
                          id="PendingInsuranceProviderBulkReject"
                          onClick={() => {
                            handleClose("dropdown1");
                            handleReject();
                          }}
                          className="w-125px"
                        >
                          <i className="fa fa-times text-danger mr-2 w-20px"></i>
                          {t("Reject")}
                        </MenuItem>
                      </PermissionComponent>
                    </StyledDropMenu>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2"></div>
              </div>
              <div className="align-items-center d-flex gap-2 justify-content-center mb-2">
                <button
                  id="PendingInsuranceProviderSearchButton"
                  onClick={() => {
                    setCurPage(1);
                    setTriggerSearchData((prev) => !prev);
                  }}
                  className="btn btn-linkedin btn-sm fw-500"
                  aria-controls="Search"
                >
                  {t("Search")}
                </button>
                <button
                  onClick={resetSearch}
                  type="button"
                  className="btn btn-secondary btn-sm btn-secondary--icon fw-500"
                  id="PendingInsuranceProviderReset"
                >
                  <span>
                    <span>{t("Reset")}</span>
                  </span>
                </button>
              </div>
            </div>

            <div className="card">
              <Box sx={{ height: "auto", width: "100%" }}>
                <div className="table_bordered overflow-hidden">
                  <TableContainer
                    sx={
                      isIphone
                        ? {}
                        : {
                            maxHeight: "calc(100vh - 100px)",
                            "&::-webkit-scrollbar": {
                              width: 7,
                            },
                            "&::-webkit-scrollbar-track": {
                              backgroundColor: "#fff",
                            },
                            "&:hover": {
                              "&::-webkit-scrollbar-thumb": {
                                backgroundColor: "var(--kt-gray-400)",
                                borderRadius: 2,
                              },
                            },
                            "&::-webkit-scrollbar-thumb": {
                              backgroundColor: "var(--kt-gray-400)",
                              borderRadius: 2,
                            },
                          }
                    }
                    className="shadow-none"
                  >
                    <Table
                      aria-label="sticky table collapsible"
                      className="table table-cutome-expend table-bordered table-sticky-header table-head-2-bg table-bg table-head-custom table-vertical-center border-0 mb-0"
                    >
                      <TableHead>
                        <TableRow className="h-50px">
                          <TableCell></TableCell>
                          <TableCell></TableCell>
                          <TableCell></TableCell>
                          <TableCell>
                            <input
                              id="PendingInsuranceProviderNameSearch"
                              type="text"
                              name="providerName"
                              className="form-control bg-white mb-3 mb-lg-0 h-30px rounded-2 fs-8 w-100"
                              placeholder={t("Search ...")}
                              value={searchRequest.providerName}
                              onChange={onInputChangeSearch}
                              onKeyDown={(e) => handleKeyPress(e)}
                            />
                          </TableCell>
                          <TableCell>
                            <input
                              id="PendingInsuranceProviderCodeSearch"
                              type="text"
                              name="providerCode"
                              className="form-control bg-white mb-3 mb-lg-0 h-30px rounded-2 fs-8 w-100"
                              placeholder={t("Search ...")}
                              value={searchRequest.providerCode}
                              onChange={onInputChangeSearch}
                              onKeyDown={(e) => handleKeyPress(e)}
                            />
                          </TableCell>
                          <TableCell>
                            <input
                              id="PendingInsuranceAddressSearch"
                              type="text"
                              name="address"
                              className="form-control bg-white mb-3 mb-lg-0 h-30px rounded-2 fs-8 w-100"
                              placeholder={t("Search ...")}
                              value={searchRequest.address}
                              onChange={onInputChangeSearch}
                              onKeyDown={(e) => handleKeyPress(e)}
                            />
                          </TableCell>
                          <TableCell>
                            <input
                              id="PendingInsuranceCitySearch"
                              type="text"
                              name="city"
                              className="form-control bg-white mb-3 mb-lg-0 h-30px rounded-2 fs-8 w-100"
                              placeholder={t("Search ...")}
                              value={searchRequest.city}
                              onChange={onInputChangeSearch}
                              onKeyDown={(e) => handleKeyPress(e)}
                            />
                          </TableCell>
                          <TableCell>
                            <input
                              id="PendingInsuranceStateSearch"
                              type="text"
                              name="state"
                              className="form-control bg-white mb-3 mb-lg-0 h-30px rounded-2 fs-8 w-100"
                              placeholder={t("Search ...")}
                              value={searchRequest.state}
                              onChange={onInputChangeSearch}
                              onKeyDown={(e) => handleKeyPress(e)}
                            />
                          </TableCell>
                          <TableCell>
                            <input
                              id="PendingInsuranceZipCodeSearch"
                              type="text"
                              name="zipCode"
                              className="form-control bg-white mb-3 mb-lg-0 h-30px rounded-2 fs-8 w-100"
                              placeholder={t("Search ...")}
                              value={searchRequest.zipCode}
                              onChange={onInputChangeSearch}
                              onKeyDown={(e) => handleKeyPress(e)}
                            />
                          </TableCell>
                          <TableCell>
                            <input
                              id="PendingInsurancePhoneNumberSearch"
                              type="number"
                              name="phoneNumber"
                              className="form-control bg-white mb-3 mb-lg-0 h-30px rounded-2 fs-8 w-100"
                              placeholder={t("Search ...")}
                              value={searchRequest.phoneNumber}
                              onChange={onInputChangeSearch}
                              onKeyDown={(e) => handleKeyPress(e)}
                            />
                          </TableCell>
                          <TableCell></TableCell>
                        </TableRow>

                        <TableRow className="h-40px">
                          <TableCell className="min-w-50px"></TableCell>
                          <TableCell>
                            <label className="form-check form-check-sm form-check-solid d-flex justify-content-center">
                              <input
                                id="PendingInsuranceProviderSelectAll"
                                className="form-check-input"
                                type="checkbox"
                                checked={selectAll}
                                onChange={(e) =>
                                  handleSelectAll(e.target.checked)
                                }
                              />
                            </label>
                          </TableCell>
                          <TableCell className="min-w-50px">
                            {t("Actions")}
                          </TableCell>
                          <TableCell sx={{ width: "max-content" }}>
                            <div
                              onClick={() => handleSort("providerName")}
                              className="d-flex justify-content-between cursor-pointer"
                              ref={searchRef}
                            >
                              <div style={{ width: "max-content" }}>
                                {t("Provider Name")}
                              </div>
                              <div className="d-flex justify-content-center align-items-center mx-4 mr-0">
                                <ArrowUp
                                  CustomeClass={`${
                                    sort.sortingOrder === "desc" &&
                                    sort.clickedIconData === "providerName"
                                      ? "text-success fs-7"
                                      : "text-gray-700 fs-7"
                                  }  p-0 m-0 "`}
                                />
                                <ArrowDown
                                  CustomeClass={`${
                                    sort.sortingOrder === "asc" &&
                                    sort.clickedIconData === "providerName"
                                      ? "text-success fs-7"
                                      : "text-gray-700 fs-7"
                                  }  p-0 m-0`}
                                />
                              </div>
                            </div>
                          </TableCell>
                          <TableCell sx={{ width: "max-content" }}>
                            <div
                              onClick={() => handleSort("providerCode")}
                              className="d-flex justify-content-between cursor-pointer"
                              ref={searchRef}
                            >
                              <div style={{ width: "max-content" }}>
                                {t("Provider Code")}
                              </div>
                              <div className="d-flex justify-content-center align-items-center mx-4 mr-0">
                                <ArrowUp
                                  CustomeClass={`${
                                    sort.sortingOrder === "desc" &&
                                    sort.clickedIconData === "providerCode"
                                      ? "text-success fs-7"
                                      : "text-gray-700 fs-7"
                                  }  p-0 m-0 "`}
                                />
                                <ArrowDown
                                  CustomeClass={`${
                                    sort.sortingOrder === "asc" &&
                                    sort.clickedIconData === "providerCode"
                                      ? "text-success fs-7"
                                      : "text-gray-700 fs-7"
                                  }  p-0 m-0`}
                                />
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div
                              onClick={() => handleSort("address")}
                              className="d-flex justify-content-between cursor-pointer"
                              ref={searchRef}
                            >
                              <div>{t("Address")}</div>
                              <div className="d-flex justify-content-center align-items-center mx-4 mr-0">
                                <ArrowUp
                                  CustomeClass={`${
                                    sort.sortingOrder === "desc" &&
                                    sort.clickedIconData === "address"
                                      ? "text-success fs-7"
                                      : "text-gray-700 fs-7"
                                  }  p-0 m-0 "`}
                                />
                                <ArrowDown
                                  CustomeClass={`${
                                    sort.sortingOrder === "asc" &&
                                    sort.clickedIconData === "address"
                                      ? "text-success fs-7"
                                      : "text-gray-700 fs-7"
                                  }  p-0 m-0`}
                                />
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div
                              onClick={() => handleSort("city")}
                              className="d-flex justify-content-between cursor-pointer"
                              ref={searchRef}
                            >
                              <div>{t("City")}</div>
                              <div className="d-flex justify-content-center align-items-center mx-4 mr-0">
                                <ArrowUp
                                  CustomeClass={`${
                                    sort.sortingOrder === "desc" &&
                                    sort.clickedIconData === "city"
                                      ? "text-success fs-7"
                                      : "text-gray-700 fs-7"
                                  }  p-0 m-0 "`}
                                />
                                <ArrowDown
                                  CustomeClass={`${
                                    sort.sortingOrder === "asc" &&
                                    sort.clickedIconData === "city"
                                      ? "text-success fs-7"
                                      : "text-gray-700 fs-7"
                                  }  p-0 m-0`}
                                />
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div
                              onClick={() => handleSort("state")}
                              className="d-flex justify-content-between cursor-pointer"
                              ref={searchRef}
                            >
                              <div>{t("State")}</div>
                              <div className="d-flex justify-content-center align-items-center mx-4 mr-0">
                                <ArrowUp
                                  CustomeClass={`${
                                    sort.sortingOrder === "desc" &&
                                    sort.clickedIconData === "state"
                                      ? "text-success fs-7"
                                      : "text-gray-700 fs-7"
                                  }  p-0 m-0 "`}
                                />
                                <ArrowDown
                                  CustomeClass={`${
                                    sort.sortingOrder === "asc" &&
                                    sort.clickedIconData === "state"
                                      ? "text-success fs-7"
                                      : "text-gray-700 fs-7"
                                  }  p-0 m-0`}
                                />
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div
                              onClick={() => handleSort("zipCode")}
                              className="d-flex justify-content-between cursor-pointer"
                              ref={searchRef}
                            >
                              <div>{t("Zip Code")}</div>
                              <div className="d-flex justify-content-center align-items-center mx-4 mr-0">
                                <ArrowUp
                                  CustomeClass={`${
                                    sort.sortingOrder === "desc" &&
                                    sort.clickedIconData === "zipCode"
                                      ? "text-success fs-7"
                                      : "text-gray-700 fs-7"
                                  }  p-0 m-0 "`}
                                />
                                <ArrowDown
                                  CustomeClass={`${
                                    sort.sortingOrder === "asc" &&
                                    sort.clickedIconData === "zipCode"
                                      ? "text-success fs-7"
                                      : "text-gray-700 fs-7"
                                  }  p-0 m-0`}
                                />
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="d-flex justify-content-between align-items-center">
                              <div>{t("Phone Number")}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="d-flex justify-content-between align-items-center">
                              <div>{t("File")}</div>
                            </div>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loading ? (
                          <TableCell colSpan={11} className="">
                            {isMobile ? <Loader /> : <Splash />}
                          </TableCell>
                        ) : rows.length ? (
                          rows?.map((row, index) => (
                            <PendingInsuranceProviderRow
                              key={row.id}
                              row={row}
                              index={index}
                              rows={rows}
                              setRows={setRows}
                              handleApprove={handleApprove}
                              handleReject={handleReject}
                              loadGridData={loadGridData}
                              selectedProviderIds={selectedProviderIds}
                              handleSelectProvider={handleSelectProvider}
                            />
                          ))
                        ) : (
                          <NoRecord colSpan={11} />
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
                {/* ==========================================================================================
                    //====================================  PAGINATION START =====================================
                    //============================================================================================ */}
                <CustomPagination
                  curPage={curPage}
                  nextPage={nextPage}
                  pageNumbers={pageNumbers}
                  pageSize={pageSize}
                  prevPage={prevPage}
                  showPage={showPage}
                  total={total}
                  totalPages={totalPages}
                />
                {/* ==========================================================================================
                    //====================================  PAGINATION END =====================================
                    //============================================================================================ */}
              </Box>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
